import { PdfToiChecklistItem } from '../models/pdf-toi.model';
import {
  deveOcultarItemChecklistLocalizacao,
  formatarDataChecklistToi,
  isLabelDataVistoriaToi,
  isLabelLocalizacaoOpcaoToi,
  isLabelLocalizacaoPrincipalToi,
  resolverChecklistAssinaturaSrc,
  resolverLocalizacaoTexto,
  resolverUrlMidiaToi,
  temChecklistAssinatura,
} from './toi-export-pdf.util';

export { deveOcultarItemChecklistLocalizacao };

export type ToiChecklistDisplayTipo = 'checkbox' | 'imagem' | 'texto';

type LabelMatcher = (normalizedLabel: string) => boolean;

/** Número da foto em labels como "Foto 6 - Numero do hidrometro"; `null` se o label não começar com "foto <n>". */
export function extrairNumeroFotoToi(label: string): number | null {
  const texto = label.trim().toLowerCase();
  if (!texto.startsWith('foto')) {
    return null;
  }
  const numero = parseInt(texto.slice('foto'.length), 10);
  return Number.isNaN(numero) ? null : numero;
}

function isFotoNumero(l: string, numero: number): boolean {
  return extrairNumeroFotoToi(l) === numero;
}

function semDoisPontosFinal(l: string): string {
  return l.endsWith(':') ? l.slice(0, -1).trim() : l;
}

/**
 * Ordem de exibição do checklist TOI na aba de detalhe.
 * Cada matcher é avaliado em sequência; o primeiro que casar define a posição.
 */
const CHECKLIST_TOI_ORDEM: LabelMatcher[] = [
  (l) => l.includes('numero da os'),
  (l) => l.includes('data vistoria'),
  (l) => l.includes('gerencia regional'),
  (l) => l.includes('unidade organizacional'),
  (l) => l.includes('unidades por categoria'),
  (l) => l.includes('endereco da unidade usuaria'),
  (l) => l.includes('matricula imovel') || l.includes('matricula do imovel'),
  (l) => l.includes('usuario cadastrado'),
  (l) => l.includes('identificador usuario') || l.includes('identificador do usuario'),
  (l) => l.includes('numeracao do hidrometro') || l.includes('numero do hidrometro'),
  (l) => l.includes('n lacre') || l.includes('no lacre'),
  (l) => l.includes('leitura retirada'),
  (l) => l.includes('leitura instalacao'),
  (l) => l === 'localizacao' || l.startsWith('localizacao'),
  (l) => l.includes('nome cliente'),
  (l) =>
    l.includes('documento apresentado') ||
    l.includes('identidade n') ||
    l.startsWith('cpf n'),
  (l) => semDoisPontosFinal(l) === 'assinatura',
  (l) => l.includes('nao for o cliente') || l.includes('parentesco'),
  (l) => l.includes('testemunha nome 1') || (l.includes('testemunha nome') && !l.includes('2')),
  (l) =>
    l.includes('doc testemunha 1') ||
    (l.includes('doc testemunha') && !l.includes('2') && !l.includes('assinatura')),
  (l) => l.includes('assinatura testemunha 1'),
  (l) => l.includes('testemunha nome 2'),
  (l) => l.includes('doc testemunha 2'),
  (l) => l.includes('assinatura testemunha 2'),
  (l) => l.includes('empregado copasa nome') || (l.includes('empregado copasa') && l.includes('nome')),
  (l) => l.includes('n pessoal') || l.includes('no pessoal') || l.includes('numero pessoal'),
  (l) => l.includes('assinatura empregado copasa'),
  (l) => isFotoNumero(l, 1),
  (l) => isFotoNumero(l, 2),
  (l) => isFotoNumero(l, 3),
  (l) => isFotoNumero(l, 4),
  (l) => isFotoNumero(l, 5),
  (l) => isFotoNumero(l, 6),
  (l) => isFotoNumero(l, 7),
  (l) => l.includes('recusou recebimento'),
  (l) => isFotoNumero(l, 8) && l.includes('entregue em maos') && !l.includes('nao entregue'),
  (l) => isFotoNumero(l, 8) && l.includes('nao entregue'),
  (l) => isFotoNumero(l, 9),
];

/** Posição da primeira regra de foto; fotos só são comparadas a partir dela. */
const INICIO_FOTOS_CHECKLIST_TOI = CHECKLIST_TOI_ORDEM.findIndex((matcher) => matcher('foto 1'));

function normalizarTexto(valor: string): string {
  return valor
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[°º]/g, 'o')
    .replace(/[\u2013\u2014–—]/g, '-')
    .replace(/\s+/g, ' ');
}

function isArquivoImagemToi(valor: string): boolean {
  return /^[0-9]{8}.+\.(jpe?g|png|gif|webp)$/i.test(valor.trim());
}

function isImagemChecklistToi(valor: string): boolean {
  const trimmed = valor.trim();
  return (
    isArquivoImagemToi(trimmed) ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:')
  );
}

function isLabelFotoToi(label: string): boolean {
  return /^foto\s/i.test(label);
}

/** Apenas campos cujo label identifica assinatura (não confundir com "Testemunha Nome", etc.). */
function isLabelAssinaturaToi(label: string): boolean {
  return /^assinatura(\s|:|$)/i.test(label);
}

function resolveChecklistOrdemIndex(label: string): number {
  const normalized = normalizarTexto(label);
  // Fotos só casam com as regras de foto, para que o texto após "Foto N" (ex.: "Numero do hidrometro") não as desloque.
  const inicio = extrairNumeroFotoToi(normalized) !== null ? INICIO_FOTOS_CHECKLIST_TOI : 0;
  const index = CHECKLIST_TOI_ORDEM.findIndex((matcher, i) => i >= inicio && matcher(normalized));
  return index >= 0 ? index : CHECKLIST_TOI_ORDEM.length;
}

export function ordenarChecklistToi(
  items: PdfToiChecklistItem[] | undefined | null
): PdfToiChecklistItem[] {
  if (!items?.length) {
    return [];
  }

  return [...items].sort((a, b) => {
    const ordemLabelA = resolveChecklistOrdemIndex(a.label ?? '');
    const ordemLabelB = resolveChecklistOrdemIndex(b.label ?? '');

    if (ordemLabelA !== ordemLabelB) {
      return ordemLabelA - ordemLabelB;
    }

    return (a.ordem ?? 0) - (b.ordem ?? 0);
  });
}

export function tipoExibicaoChecklistToi(item: PdfToiChecklistItem): ToiChecklistDisplayTipo {
  if (isChecklistToiCheckbox(item)) {
    return 'checkbox';
  }

  if (isChecklistToiImagem(item)) {
    return 'imagem';
  }

  return 'texto';
}

export function isChecklistToiImagem(item: PdfToiChecklistItem): boolean {
  const label = item.label?.trim() ?? '';
  const valor = item.valor ? item.valor.trim().replace(/[\t\n\r]/g, ' ').replace(/\s+/g, ' ') : '';

  if (!valor) {
    return false;
  }

  if (isImagemChecklistToi(valor)) {
    return true;
  }

  if (valor.startsWith('http://') || valor.startsWith('https://') || valor.startsWith('data:')) {
    return true;
  }

  if (isLabelFotoToi(label) && isImagemChecklistToi(valor)) {
    return true;
  }

  if (isLabelAssinaturaToi(label) && temChecklistAssinatura(valor)) {
    return true;
  }

  return false;
}

export function isChecklistToiCheckbox(item: PdfToiChecklistItem): boolean {
  const label = item.label ?? '';
  if (isLabelLocalizacaoPrincipalToi(label) || isLabelLocalizacaoOpcaoToi(label)) {
    return false;
  }

  const valor = normalizarTexto(item.valor ?? '');
  return ['sim', 'nao', 'não', 'x', 'true', 'false', '1', '0'].includes(valor);
}

export function checklistToiMarcado(item: PdfToiChecklistItem): boolean {
  const valor = normalizarTexto(item.valor ?? '');
  return valor === 'sim' || valor === 'x' || valor === 'true' || valor === '1';
}

export function checklistToiImagemSrc(item: PdfToiChecklistItem): string | null {
  const valor = item.valor ? item.valor.trim().replace(/[\t\n\r]/g, ' ').replace(/\s+/g, ' ') : '';
  if (!valor) {
    return null;
  }

  const assinatura = resolverChecklistAssinaturaSrc(valor);
  if (assinatura) {
    return assinatura;
  }

  if (isArquivoImagemToi(valor) || valor.startsWith('http://') || valor.startsWith('https://')) {
    return resolverUrlMidiaToi(valor);
  }

  return null;
}

export interface ChecklistToiValorTextoContext {
  checklist?: PdfToiChecklistItem[] | null;
  localizacaoAutomatica?: string | null;
}

export function checklistToiValorTexto(
  item: PdfToiChecklistItem,
  context?: ChecklistToiValorTextoContext
): string {
  if (isLabelLocalizacaoPrincipalToi(item.label ?? '')) {
    return resolverLocalizacaoTexto(context?.checklist, context?.localizacaoAutomatica) || '-';
  }

  const valor = item.valor ? item.valor.trim().replace(/[\t\n\r]/g, ' ').replace(/\s+/g, ' ') : '';
  if (!valor) {
    return '-';
  }

  if (isLabelDataVistoriaToi(item.label ?? '')) {
    return formatarDataChecklistToi(valor) || '-';
  }

  return valor;
}
